import React from "react";
import { X, CalendarDays, Clock, MapPin, User as UserIcon, BadgeCheck } from "lucide-react";

// ── Direct-booking detail (2026-08) ─────────────────────────────────────────
// THE single read-only detail for calendar items of type "direct_booking" —
// appointments booked straight through /book with no scheduling request, so
// the existing request detail modals (whose affordances are request actions:
// confirm / suggest / cancel / reschedule) don't fit. Consumed by BOTH
// portals (NetworkScheduling + ClinicScheduling); never fork or copy per
// portal — same doctrine as the shared month grid and week grid.
// Strictly read-only: no actions, no network calls.

function fmt12(hms) {
  if (!hms) return "";
  const [h, m] = hms.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m} ${hr >= 12 ? "PM" : "AM"}`;
}

export function DirectBookingDetailModal({ open, item, onClose }) {
  if (!open || !item) return null;
  const b = item.booking || {};
  const t = (item.proposed_times || [])[0] || {};
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4"
      data-testid="direct-booking-detail-modal"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900" data-testid="direct-detail-patient">
                {item.patient_name || `Patient #${item.patient_id}`}
              </h2>
              <span
                className="text-[11px] px-2 py-0.5 rounded-full border font-medium bg-indigo-100 text-indigo-800 border-indigo-300"
                data-testid="direct-detail-badge"
              >
                Direct
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Booked directly — not via a scheduling request</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="direct-detail-close">
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm">
          <div className="flex items-center gap-2 text-slate-700" data-testid="direct-detail-when">
            <CalendarDays size={14} className="text-slate-400" />
            <span className="font-medium">
              {t.date
                ? new Date(`${t.date}T00:00:00`).toLocaleDateString("en-US", {
                    weekday: "short", month: "short", day: "numeric", year: "numeric",
                  })
                : "—"}
            </span>
            <Clock size={14} className="text-slate-400 ml-2" />
            <span>
              {fmt12(t.start_time)} – {fmt12(t.end_time)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-slate-700" data-testid="direct-detail-location">
            <MapPin size={14} className="text-slate-400" />
            <span>{b.location_name || item.location_code || "—"}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-700" data-testid="direct-detail-type">
            <BadgeCheck size={14} className="text-slate-400" />
            <span>{item.appointment_type || "—"}</span>
          </div>
          {b.booked_by && (
            <div className="flex items-center gap-2 text-slate-700" data-testid="direct-detail-booked-by">
              <UserIcon size={14} className="text-slate-400" />
              <span>Booked by {b.booked_by}</span>
            </div>
          )}
          {/* 2026-08-31: clinic cancel/reschedule covers appointments that
              came from a scheduling request. These came straight from
              WestStar with no request behind them, so there is nothing for
              the clinic endpoints to act on — say so plainly instead of
              showing dead buttons. */}
          <p
            className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600"
            data-testid="direct-detail-readonly-note"
          >
            This appointment was booked by WestStar — contact them to change it.
          </p>
        </div>
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
            data-testid="direct-detail-close-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
